import * as React from "react";
import { SearchableSelect, type SelectOption } from "./SearchableSelect";
import { LabeledInput, useFormPrimitiveStyles } from "../formPrimitives";
import { fetchLegalFormOptions } from "../../../utils/legalFormOptions";
import staticForms from "../../../data/geselllschaftsform.json";
import { PLACEHOLDERS } from "../validation";

/**
 * Rechtsform-Auswahl (suchbar). Optionen aus dem Backend
 * (fetchLegalFormOptions, contact-legal-form) mit statischem Fallback
 * (geselllschaftsform.json). Schreibt den Rechtsform-String UND – falls aus dem
 * Backend bekannt – die legalFormId (reaktiviert die {id,legalForm}-Payload).
 *
 * Für Katalogeinträge, die selbst keine Rechtsform benennen — „Sonstige" und
 * „Ausländische Rechtsform" — blendet sie zusätzlich ein Bezeichnungsfeld ein.
 * Welche Einträge das sind, sagt der Katalog über `erlaubtFreitext`; es steht
 * nicht im Code.
 */
export function LegalFormSelect({
  label,
  name,
  value,
  onChange,
  required,
  freitext,
  onFreitextChange,
  hinweis,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (legalForm: string, id?: string) => void;
  required?: boolean;
  /**
   * Kurzer Hinweis unter dem Feld — für die Rechtsform-Erkennung aus dem
   * Firmennamen, wenn der Treffer unsicher ist und deshalb nicht gesetzt wird.
   */
  hinweis?: string | null;
  /**
   * Konkrete Bezeichnung bei „Sonstige"/„Ausländische Rechtsform". Das Feld
   * erscheint nur, wenn der Aufrufer `onFreitextChange` mitgibt — ohne das
   * verhält sich die Auswahl wie bisher. Zielfeld ist `Contact.rechtsform`.
   */
  freitext?: string;
  onFreitextChange?: (wert: string) => void;
}) {
  const [options, setOptions] = React.useState<SelectOption[]>(() =>
    (staticForms as string[]).map((f) => ({ value: f, label: f })),
  );
  const idByForm = React.useRef<Record<string, string>>({});
  const [freitextForms, setFreitextForms] = React.useState<Set<string>>(() => new Set());
  const s = useFormPrimitiveStyles();

  React.useEffect(() => {
    let alive = true;
    fetchLegalFormOptions()
      .then((list) => {
        if (!alive || !list.length) return;
        const map: Record<string, string> = {};
        for (const o of list) map[o.legalForm] = String(o.id);
        idByForm.current = map;
        setOptions(list.map((o) => ({ value: o.legalForm, label: o.legalForm })));
        setFreitextForms(new Set(list.filter((o) => o.erlaubtFreitext).map((o) => o.legalForm)));
      })
      .catch(() => {
        /* Backend nicht erreichbar → statische Liste bleibt */
      });
    return () => {
      alive = false;
    };
  }, []);

  // „Sonstige" allein sagt nichts aus. Verlangt der gewählte Katalogeintrag eine
  // Bezeichnung, wird sie hier erfasst — statt im Firmennamen zu landen oder
  // ganz verloren zu gehen.
  const zeigeFreitext = !!onFreitextChange && freitextForms.has(value);

  return (
    <>
      <SearchableSelect
        label={label}
        name={name}
        value={value}
        required={required}
        options={options}
        placeholder={PLACEHOLDERS.legalForm}
        allowFreeText
        onChange={(v) => onChange(v, idByForm.current[v])}
      />
      {!!hinweis && <div className={s.hintMsg}>{hinweis}</div>}
      {zeigeFreitext && (
        <LabeledInput
          label="Rechtsform (Bezeichnung)"
          name={`${name}-freitext`}
          value={freitext ?? ""}
          onChange={(v) => onFreitextChange?.(v)}
          placeholder="z. B. Ltd., B.V., S.A. oder eigene Bezeichnung"
        />
      )}
    </>
  );
}

export default LegalFormSelect;
